import React, { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { resolveCommonAction } from '../../i18n/labels';

interface DetailField {
  label: string;
  value: ReactNode;
  condition?: boolean;
}

export interface DetailSection {
  title: string;
  fields?: DetailField[];
  /** When set, this node is rendered instead of fields (for custom blocks e.g. nested lists). */
  content?: ReactNode;
  condition?: boolean;
}

export type DetailViewLayout = 'card' | 'compact' | 'minimal';

interface DetailViewProps {
  title: string | null | undefined;
  subtitle?: string;
  backRoute: string;
  editRoute: string;
  statusBadges?: ReactNode;
  sections: DetailSection[];
  layout?: DetailViewLayout;
  isLoading?: boolean;
  notFoundTitle?: string;
  notFoundMessage?: string;
  notFoundBackText?: string;
  canEdit?: boolean;
  /** When set, shows a delete control (requires onDelete). */
  canDelete?: boolean;
  onDelete?: () => void;
  /** Optional actions to show in the header next to Edit (e.g. Client summary link) */
  extraHeaderActions?: ReactNode;
}

const DetailView: React.FC<DetailViewProps> = ({
  title,
  subtitle,
  backRoute,
  editRoute,
  statusBadges,
  sections,
  layout = 'card',
  isLoading,
  notFoundTitle,
  notFoundMessage,
  notFoundBackText,
  canEdit = true,
  canDelete = false,
  onDelete,
  extraHeaderActions,
}) => {
  const { t } = useTranslation('common');
  const { colors } = useTheme();
  const navigate = useNavigate();

  const resolvedNotFoundTitle = notFoundTitle ?? t('states.notFoundTitle');
  const resolvedNotFoundMessage = notFoundMessage ?? t('states.notFoundMessage');
  const resolvedNotFoundBackText = notFoundBackText
    ? resolveCommonAction(t, notFoundBackText)
    : t('actions.backToList');

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(backRoute);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className={cn('text-center', colors.text)}>
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent" />
          <p className={cn('mt-3 text-sm', colors.textMuted)}>{t('states.loading')}</p>
        </div>
      </div>
    );
  }

  if (!title && !isLoading) {
    return (
      <div className={cn('rounded-xl border py-12 text-center', colors.surface, colors.border)}>
        <h2 className={cn('text-lg font-semibold', colors.text)}>{resolvedNotFoundTitle}</h2>
        <p className={cn('mt-2 text-sm', colors.textMuted)}>{resolvedNotFoundMessage}</p>
        <Link
          to={backRoute}
          className={cn(
            'mt-4 inline-flex items-center rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
            colors.border,
            colors.interactiveHover,
            colors.text
          )}
        >
          {resolvedNotFoundBackText}
        </Link>
      </div>
    );
  }

  if (!title) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header
        className={cn(
          'flex-shrink-0 border-b',
          colors.border,
          colors.headerBg
        )}
      >
        <div className="px-4 py-5 sm:px-6">
          {/* Top row: back + actions */}
          <div className="flex items-center justify-between gap-3 pb-4">
            <button
              type="button"
              onClick={handleBack}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors -ml-2',
                colors.textMuted,
                colors.interactiveHover,
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400/30 dark:focus:ring-gray-500/40'
              )}
              title={t('actions.back')}
            >
              <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="hidden sm:inline">{t('actions.back')}</span>
            </button>
            {(canEdit || canDelete || extraHeaderActions) && (
              <div className={cn('flex items-center gap-2 rounded-xl border py-1 pl-2 pr-4', colors.border, colors.surface)}>
                {extraHeaderActions}
                {canEdit && (
                  <Link
                    to={editRoute}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      colors.primary,
                      colors.primaryText,
                      'hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500/70'
                    )}
                    title={t('actions.edit')}
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>{t('actions.edit')}</span>
                  </Link>
                )}
                {canDelete && onDelete ? (
                  <button
                    type="button"
                    onClick={onDelete}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      'text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30',
                      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500/50'
                    )}
                    title={t('actions.delete')}
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>{t('actions.delete')}</span>
                  </button>
                ) : null}
              </div>
            )}
          </div>
          {/* Title block */}
          <div className="min-w-0">
            <h1 className={cn('text-xl font-semibold tracking-tight sm:text-2xl', colors.text)}>
              {title}
            </h1>
            {subtitle != null && subtitle !== '' && (
              <p
                className={cn('mt-0.5 text-sm truncate max-w-full', colors.textMuted)}
                title={typeof subtitle === 'string' && subtitle.length > 50 ? subtitle : undefined}
              >
                {typeof subtitle === 'string'
                  ? subtitle.length > 50
                    ? `${subtitle.slice(0, 50).trim()}...`
                    : subtitle
                  : subtitle}
              </p>
            )}
            {statusBadges && (
              <div className="mt-3 flex flex-wrap gap-2">{statusBadges}</div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-2 pt-6 pb-6">
        {layout === 'compact' && <CompactSections sections={sections} colors={colors} />}
        {layout === 'minimal' && <MinimalSections sections={sections} colors={colors} />}
        {layout === 'card' && <CardSections sections={sections} colors={colors} />}
      </div>
    </div>
  );
};

function renderFieldValue(
  field: DetailField,
  colors: ReturnType<typeof useTheme>['colors']
): { isEmpty: boolean; displayValue: ReactNode } {
  const isReactNode = React.isValidElement(field.value);
  const isEmpty =
    !isReactNode &&
    (field.value == null ||
      field.value === '' ||
      (typeof field.value === 'string' && (field.value as string).trim() === ''));
  const displayValue =
    isReactNode ||
    typeof field.value !== 'object' ||
    field.value === null
      ? field.value
      : JSON.stringify(field.value);
  return {
    isEmpty,
    displayValue: isEmpty ? null : displayValue,
  };
}

function CardSections({
  sections,
  colors,
}: {
  sections: DetailSection[];
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  const filtered = sections.filter((s) => s.condition !== false);
  const singleSection = filtered.length === 1;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {filtered.map((section, index) => (
            <div
              key={index}
              className={cn(
                'overflow-hidden rounded-xl border border-l-4 shadow-sm',
                singleSection && 'lg:col-span-2',
                colors.surface,
                colors.border,
                colors.primaryBorder
              )}
            >
              {section.title ? (
                <div
                  className={cn(
                    'border-b px-5 py-4',
                    colors.border,
                    colors.surfaceHover
                  )}
                >
                  <h2 className={cn('text-sm font-semibold uppercase tracking-wider', colors.textMuted)}>
                    {section.title}
                  </h2>
                </div>
              ) : null}
              {section.content != null ? (
                <div className="px-5 py-4">{section.content}</div>
              ) : (
                <dl className={cn('divide-y', colors.divide)}>
                  {(section.fields ?? [])
                    .filter((f) => f.condition !== false)
                    .map((field, fieldIndex) => {
                      const { isEmpty, displayValue } = renderFieldValue(field, colors);
                      if (isEmpty) return null;
                      const noCaption = !field.label?.trim();
                      return (
                        <div
                          key={fieldIndex}
                          className={cn(
                            'px-5 py-3 sm:py-3.5',
                            noCaption ? '' : 'sm:grid sm:grid-cols-3 sm:gap-4'
                          )}
                        >
                          {!noCaption && (
                            <dt className={cn('text-sm font-medium', colors.textMuted)}>
                              {field.label}
                            </dt>
                          )}
                          <dd
                            className={cn(
                              'text-sm',
                              colors.text,
                              noCaption ? 'w-full' : 'mt-1 sm:col-span-2 sm:mt-0'
                            )}
                          >
                            {displayValue}
                          </dd>
                        </div>
                      );
                    })}
                </dl>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

function CompactSections({
  sections,
  colors,
}: {
  sections: DetailSection[];
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <div className="mx-auto max-w-3xl space-y-10">
      {sections
        .filter((s) => s.condition !== false)
        .map((section, index) => (
          <section key={index}>
            <h2
              className={cn(
                'border-b pb-2 text-base font-semibold',
                colors.text,
                colors.border
              )}
            >
              {section.title}
            </h2>
            {section.content != null ? (
              <div className="mt-4">{section.content}</div>
            ) : (
              <dl className="mt-4 space-y-1">
                {(section.fields ?? [])
                  .filter((f) => f.condition !== false)
                  .map((field, fieldIndex) => {
                    const { isEmpty, displayValue } = renderFieldValue(field, colors);
                    if (isEmpty) return null;
                    const noCaption = !field.label?.trim();
                    return (
                      <div
                        key={fieldIndex}
                        className={cn(
                          'flex flex-wrap gap-x-3 gap-y-1 py-2',
                          noCaption ? '' : 'sm:flex-nowrap'
                        )}
                      >
                        {!noCaption && (
                          <dt className={cn('w-40 flex-shrink-0 text-sm', colors.textMuted)}>
                            {field.label}
                          </dt>
                        )}
                        <dd
                          className={cn(
                            'text-sm',
                            colors.text,
                            noCaption ? 'w-full min-w-0' : 'min-w-0 flex-1'
                          )}
                        >
                          {displayValue}
                        </dd>
                      </div>
                    );
                  })}
              </dl>
            )}
          </section>
        ))}
    </div>
  );
}

function MinimalSections({
  sections,
  colors,
}: {
  sections: DetailSection[];
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <div className="mx-auto max-w-2xl space-y-8">
      {sections
        .filter((s) => s.condition !== false)
        .map((section, index) => (
          <section key={index}>
            <h2 className={cn('text-xs font-medium uppercase tracking-widest', colors.textMuted)}>
              {section.title}
            </h2>
            {section.content != null ? (
              <div className="mt-3">{section.content}</div>
            ) : (
              <dl className="mt-3 space-y-0">
                {(section.fields ?? [])
                  .filter((f) => f.condition !== false)
                  .map((field, fieldIndex) => {
                    const { isEmpty, displayValue } = renderFieldValue(field, colors);
                    if (isEmpty) return null;
                    const noCaption = !field.label?.trim();
                    return (
                      <div
                        key={fieldIndex}
                        className="flex gap-3 py-1.5 text-sm"
                      >
                        {!noCaption && (
                          <dt className={cn('w-32 flex-shrink-0', colors.textMuted)}>
                            {field.label}
                          </dt>
                        )}
                        <dd
                          className={cn(
                            colors.text,
                            noCaption ? 'min-w-0 w-full' : 'min-w-0 flex-1'
                          )}
                        >
                          {displayValue}
                        </dd>
                      </div>
                    );
                  })}
              </dl>
            )}
          </section>
        ))}
    </div>
  );
}

export default DetailView;
